<template>
  <el-form
    ref="domain"
    id="domain-connect"
    size="small"
    :model="form"
    :rules="rules"
    inline
  >
    <template v-if="result">
      <el-page-header title="cancel" :content="result.domain" @back="cancel" />

      <el-row class="result">
        <el-col :span="14" class="available">
          <h2>
            <i class="mdi mdi-check" />

            {{ result.domain }}
          </h2>
        </el-col>

        <el-col :span="10" align="right">
          <el-checkbox
            v-model="form.use"
            :true-label="result.domain"
            :disabled="loading"
            size="large"
            border
          >
            Use this domain
          </el-checkbox>
        </el-col>
      </el-row>

      <el-card v-if="form.use" class="dns" shadow="never">
        <h5 slot="header">DNS management for your application :</h5>
        <el-form-item
          v-for="(domain, index) in form.domains"
          :key="domain.key"
          :prop="'domains.' + index + '.value'"
          :rules="[{ validator: checkDns, trigger: 'blur' }]"
        >
          <el-input v-model="domain.value" :disabled="loading" placeholder="Use empty string for root">
            <template slot="append">.{{ domain.name }}</template>
          </el-input>
          <el-button v-if="index > 0 && !loading" @click.prevent="remove(domain)">Delete</el-button>
        </el-form-item>

        <el-button
          type="info"
          size="mini"
          icon="mdi mdi-plus"
          :disabled="loading"
          @click="add(result.domain)"
        >
          Add record
        </el-button>

        <el-alert v-if="!result.available" type="error" :closable="false">
          <div slot="title">
            <i class="mdi mdi-alert" />
            you will have to configure the ip address manually, make sure you have the necessary rights.
          </div>
        </el-alert>

        <el-alert
          v-else
          type="info"
          title="this domain seem to not be currently active or registred."
          :closable="false"
          show-icon
        />
      </el-card>
    </template>

    <template v-else>
      <el-form-item class="input" prop="check" :error="error">
        <el-input
          v-model="form.check"
          :placeholder="placeholder"
          :disabled="search"
          @keypress.enter="check"
          clearable
        />
      </el-form-item>

      <el-form-item>
        <el-button
          type="info"
          size="small"
          icon="mdi mdi-arrow-right"
          :loading="search"
          @click.prevent="check"
        >
          {{ label }}
        </el-button>
      </el-form-item>
    </template>
  </el-form>
</template>

<script>
import { mapState } from 'vuex'
import isValidDomain from 'is-valid-domain'

export default {
  props: {
    connect: {
      type: Array,
      default: () => []
    },
    useCancel: Boolean,
    loading: Boolean,
    placeholder: {
      type: String,
      default: 'Choose your primary domain name to start (ex: runstack.com)'
    },
    label: {
      type: String,
      default: 'Check availability'
    }
  },
  data () {
    const checkDomain = (rule, value, done) => {
      if (!value || value.trim() === '') {
        return done()
      }
      if (isValidDomain(value.trim())) {
        return done()
      }
      return done('You must enter only the chosen domain name (without the https: //).')
    }
    const checkDns = (rule, value, done) => checkDomain(rule, value ? value + '.' + this.result.domain : null, done)
    return {
      checkDns,
      error: null,
      result: null,
      search: false,
      form: {
        check: null,
        use: null,
        domains: []
      },
      rules: {
        domains: [{ validator: checkDomain, trigger: 'blur' }]
      }
    }
  },
  computed: mapState(['domains', 'project']),
  methods: {
    check () {
      this.$refs.domain.validate((valid) => {
        if (valid) {
          this.search = true
          this.$store.dispatch('checkDomain', this.form.check).then((result) => {
            this.result = result
            this.search = false
            this.form.use = result.root
            this.form.check = ''
            this.form.domains = [{ name: result.root, key: 1, value: result.subname }]

            if (result.root === result.domain) {
              this.form.domains.push({ name: result.root, key: 1, value: 'www' })
            }
          }).catch(() => {
            this.search = false
            this.error = 'There was a problem with the domain name you chose. Please check name or extension.'
          })
        }
      })
    },
    cancel () {
      this.result = null
      this.form.use = null
      this.form.domains = []
    },
    remove (item) {
      const index = this.form.domains.indexOf(item)
      if (index !== -1) {
        this.form.domains.splice(index, 1)
      }
    },
    add (name) {
      this.form.domains.push({ name, key: Date.now(), value: '' })
    }
  },
  watch: {
    'result' () {
      this.error = null
    },
    'form.use' (name) {
      if (!name) {
        this.form.domains = []
        if (this.useCancel) {
          this.result = null
        }
      }
    },
    'form.domains': {
      handler (value) {
        this.$nextTick(this.$refs.domain.validate)
        this.$emit('update:connect', value.map((d) => {
          const name = d.value.trim()
          return name ? name + '.' + this.result.root : this.result.root
        }))
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../theme";

  #domain-connect {
    .el-form-item {
      margin-right: 0;

      &.input {
        width: calc(100% - 185px);
        margin-right: 10px;

        .el-input {
          width: 100%;
        }
      }
    }
    h2, h5 {
      margin: 0;
    }
    .available {
      color: $--color-primary;
    }
    .dns {
      margin-top: 15px;

      .el-form-item {
        width: 100%;

        .el-input {
          width: calc(100% - 200px);
          margin-right: 10px;
        }
      }
    }
    .el-button--info.el-button--small {
      width: 175px;
    }
  }
</style>

<style lang="scss">
  #instance {
    #domain-connect {
      .el-form-item__content {
        width: 100%;
      }

      .el-page-header {
        margin-top: -20px;
        margin-right: -20px;

        .el-page-header__content {
          font-size: 13px;
        }
        .el-page-header__title {
          font-size: 12px;
        }
      }

      .dns {
        .el-card__body {
          padding-bottom: 50px;
          position: relative;

          > .el-alert {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            -moz-border-radius-topleft: 0;
            -moz-border-radius-topright: 0;
            -webkit-border-top-left-radius: 0;
            -webkit-border-top-right-radius: 0;
            border-top-left-radius: 0;
            border-top-right-radius: 0;
          }
        }
      }
    }
  }
</style>
